import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code as ConnectCode, ConnectError } from '@connectrpc/connect';
import { SpecraButton, SpecraCheckbox, SpecraDataTable, SpecraEyebrow, SpecraHeading, SpecraIconTile, SpecraInlineBanner, SpecraKeyValueRow, SpecraSurface, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Check, FileSpreadsheet } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { agentClient, contractModelClient } from '../../../lib/connect/clients';
import { useChatFeedback } from '../ChatFeedbackContext';
import { EvidenceGapsSection } from './EvidenceGapsSection';
import { PROPOSAL_DECIDED_EVENT, recalledDecision, rememberDecision } from './proposalDecisions';
const ASSIST_HEADERS = {
    'X-Specra-Via': 'assist'
};
const REASONS = {
    NO_TEAM_LABEL: 'No team on the row',
    UNKNOWN_TEAM: 'Team not in this program',
    AMBIGUOUS_TEAM: 'Team label fits several teams',
    NO_TITLE: 'No deliverable title',
    UNKNOWN_DUTY: 'Deliverable not on this team',
    AMBIGUOUS_DUTY: 'Title fits several deliverables',
    NO_DUE_DATE: 'No due date',
    NO_OCCURRENCE_ON_DATE: 'No date near the stated one',
    OCCURRENCE_TAKEN: 'Date already filed by another row'
};
function n(v) {
    return typeof v === 'number' ? v : 0;
}
function importErrorMessage(err) {
    if (err instanceof ConnectError) {
        switch(err.code){
            case ConnectCode.PermissionDenied:
                return err.rawMessage ? `${err.rawMessage.charAt(0).toUpperCase()}${err.rawMessage.slice(1)}.` : "You don't have access to file deliverables here. Reject it, or ask someone who can.";
            case ConnectCode.NotFound:
            case ConnectCode.FailedPrecondition:
                return err.rawMessage || 'The program changed since this was staged. Ask the assistant to stage it again.';
            default:
                return "It couldn't be filed right now. Retry, or reject it.";
        }
    }
    return "It couldn't be filed right now. Retry, or reject it.";
}
/** The applied plan as the card's view of it. */ function planView(applied) {
    return {
        submissionsNew: applied.submissionsNew,
        submissionsExisting: applied.submissionsExisting,
        reviewsNew: applied.reviewsNew,
        moves: applied.moves,
        extensions: applied.extensions,
        extensionsInstant: applied.extensionsInstant,
        instantApprover: applied.instantApprover,
        adjustedSkipped: applied.adjustedSkipped,
        datesNew: applied.datesNew,
        conflicts: applied.conflicts.length,
        events: applied.events?.length ?? 0
    };
}
/** One line on what applying wrote, for the card and the agent. */ export function evidenceReceipt(plan, withReviews) {
    const parts = [
        `${n(plan.submissionsNew)} submissions filed`
    ];
    if (n(plan.datesNew)) parts.push(`${n(plan.datesNew)} dates logged by their filings`);
    if (n(plan.events)) parts.push(`${n(plan.events)} events logged from the records' due dates`);
    if (withReviews) parts.push(`${n(plan.reviewsNew)} approvals recorded`);
    if (n(plan.moves)) parts.push(`${n(plan.moves)} dates moved to the evidence's adjusted date`);
    if (n(plan.extensions)) parts.push(`${n(plan.extensions)} extensions proposed for a second approver`);
    if (n(plan.extensionsInstant)) parts.push(`${n(plan.extensionsInstant)} extended now (instant: ${plan.instantApprover})`);
    if (n(plan.adjustedSkipped)) parts.push(`${n(plan.adjustedSkipped)} adjusted dates left as they were`);
    if (n(plan.submissionsExisting)) parts.push(`${n(plan.submissionsExisting)} already on file`);
    if (n(plan.conflicts)) parts.push(`${n(plan.conflicts)} left as questions`);
    return `${parts.join(', ')}.`;
}
/**
 * Asks before chat files deliverable evidence from any source: the counts of
 * what one Accept writes by kind (filings, approvals, adjusted dates; the
 * last two can be unticked), the records it could not match and why, and,
 * for complete evidence, the past-due dates it never mentions. Accept
 * applies the staged run in one call as the user; Reject writes nothing. A
 * run applied already shows what it wrote, never Accept again.
 */ export function EvidenceBatchCard({ output, toolCallId }) {
    const sendFeedback = useChatFeedback();
    const recalled = recalledDecision(toolCallId);
    const saved = recalled?.decision ?? output.approvalState;
    const [status, setStatus] = useState(saved === 'accepted' ? 'accepted' : saved === 'rejected' ? 'rejected' : 'pending');
    const [receipt, setReceipt] = useState(recalled?.receipt ?? '');
    const [error, setError] = useState(null);
    // The kinds the user keeps: approvals and adjusted dates can be left out.
    const [keepReviews, setKeepReviews] = useState(true);
    const [keepAdjusted, setKeepAdjusted] = useState(true);
    // The run itself is the truth: a saved "pending" whose run is applied
    // (the decision was never recorded) must not offer Accept again.
    const [runChecked, setRunChecked] = useState(false);
    const actingRef = useRef(false);
    const { programId, runId, reviewsPermitted } = output;
    // biome-ignore lint/correctness/useExhaustiveDependencies: read once per run; later status changes are the card's own
    useEffect(()=>{
        if (status !== 'pending' || !programId || !runId) {
            setRunChecked(true);
            return;
        }
        let live = true;
        contractModelClient.importTracker({
            body: {
                programId,
                runId,
                dryRun: true
            }
        }, {
            headers: ASSIST_HEADERS
        }).then((resp)=>{
            const applied = resp.body?.applied ? resp.body.plan : undefined;
            if (!live || !applied) return;
            setReceipt(evidenceReceipt(planView(applied), Boolean(reviewsPermitted)));
            setStatus('accepted');
        }).catch(()=>{
        // Unread: Accept stays, and applying an applied run writes nothing.
        }).finally(()=>{
            if (live) setRunChecked(true);
        });
        return ()=>{
            live = false;
        };
    }, [
        programId,
        runId
    ]);
    useEffect(()=>{
        const onDecided = ()=>{
            const d = recalledDecision(toolCallId);
            if (!d) return;
            setStatus(d.decision);
            setReceipt(d.receipt ?? '');
        };
        window.addEventListener(PROPOSAL_DECIDED_EVENT, onDecided);
        return ()=>window.removeEventListener(PROPOSAL_DECIDED_EVENT, onDecided);
    }, [
        toolCallId
    ]);
    if (output.success === false) {
        return /*#__PURE__*/ _jsx(SpecraInlineBanner, {
            variant: "framed",
            tone: "danger",
            "data-testid": Testids.assistant.evidenceBatchCard,
            children: output.message || 'The evidence could not be staged.'
        });
    }
    const plan = output.plan ?? {};
    const mayReview = Boolean(output.reviewsPermitted);
    const withReviews = mayReview && keepReviews;
    const adjusted = n(plan.moves) + n(plan.extensions) + n(plan.extensionsInstant);
    const decidable = Boolean(output.runId && output.approvalConversationId && toolCallId);
    const decide = async (decision)=>{
        if (actingRef.current || status !== 'pending' || !decidable) return;
        actingRef.current = true;
        setStatus('working');
        setError(null);
        try {
            if (decision === 'rejected') {
                await agentClient.decideDocumentProposal({
                    conversationId: output.approvalConversationId,
                    toolCallId: toolCallId,
                    decision: 'rejected'
                });
                rememberDecision(toolCallId, {
                    decision: 'rejected'
                });
                setStatus('rejected');
                sendFeedback('[evidence-batch] rejected: nothing was filed. Ask what they want changed.');
                return;
            }
            const resp = await contractModelClient.importTracker({
                body: {
                    programId: output.programId ?? '',
                    runId: output.runId ?? '',
                    includeReviews: withReviews,
                    skipAdjusted: !keepAdjusted
                }
            }, {
                headers: ASSIST_HEADERS
            });
            const applied = resp.body?.plan;
            const line = applied ? evidenceReceipt(planView(applied), withReviews) : 'Filed.';
            // The run is applied: the card says so even if recording the decision
            // fails (a reload reads the run and still shows it filed).
            rememberDecision(toolCallId, {
                decision: 'accepted',
                receipt: line
            });
            setReceipt(line);
            setStatus('accepted');
            await agentClient.decideDocumentProposal({
                conversationId: output.approvalConversationId,
                toolCallId: toolCallId,
                decision: 'accepted',
                rowResults: [
                    {
                        status: 'ok',
                        message: line
                    }
                ]
            }).catch(()=>undefined);
            sendFeedback(`[evidence-batch] applied: ${line}`);
        } catch (err) {
            setStatus('pending');
            setError(importErrorMessage(err));
        } finally{
            actingRef.current = false;
        }
    };
    const shapes = (output.unmatchedShapes ?? []).map((s, i)=>({
            id: String(i),
            ...s
        }));
    const unfiled = (output.unfiledSample ?? []).map((u, i)=>({
            id: u.occurrenceId || String(i),
            ...u
        }));
    const accepted = status === 'accepted';
    const matched = n(output.matched?.code) + n(output.matched?.jev);
    const eventGroups = output.events ?? [];
    const eventCount = eventGroups.reduce((sum, g)=>sum + n(g.count), 0);
    const kindTick = (kind, label, checked, set)=>/*#__PURE__*/ _jsx(SpecraCheckbox, {
            "data-testid": Testids.assistant.evidenceBatchKind,
            "data-kind": kind,
            "aria-label": `Include ${label}`,
            checked: checked,
            disabled: status !== 'pending',
            onCheckedChange: (c)=>set(Boolean(c))
        });
    return /*#__PURE__*/ _jsxs(SpecraSurface, {
        "data-testid": Testids.assistant.evidenceBatchCard,
        "data-state": status,
        elevation: "xs",
        className: "overflow-hidden",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-start gap-3 px-4 pb-3 pt-3.5",
                children: [
                    /*#__PURE__*/ _jsx(SpecraIconTile, {
                        tone: accepted ? 'success' : 'info',
                        children: /*#__PURE__*/ _jsx(FileSpreadsheet, {
                            className: "h-4 w-4"
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "min-w-0 flex-1",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraEyebrow, {
                                children: accepted ? 'Filed' : 'Needs your OK · nothing filed yet'
                            }),
                            /*#__PURE__*/ _jsxs(SpecraHeading, {
                                size: "sm",
                                className: "mt-1",
                                children: [
                                    "File evidence · ",
                                    output.source || 'records'
                                ]
                            })
                        ]
                    })
                ]
            }),
            output.restagedNote ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                variant: "band",
                tone: "warning",
                "data-testid": Testids.assistant.evidenceBatchRestaged,
                children: output.restagedNote
            }) : null,
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-1 border-t border-border px-4 py-2.5",
                children: [
                    /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                        label: "Records",
                        value: `${n(output.rows)} · ${matched} matched to a date (${n(output.matched?.jev)} by Jev) · ${n(output.unmatched)} not`
                    }),
                    /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                        label: "Accept files",
                        value: `${n(plan.submissionsNew)} submissions${n(plan.submissionsExisting) ? ` (${n(plan.submissionsExisting)} already on file)` : ''}${n(plan.datesNew) ? ` · ${n(plan.datesNew)} dates logged by their filings (as-needed, event or unscheduled deliverables)` : ''}`
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            mayReview && n(plan.reviewsNew) ? kindTick('approvals', 'approvals', keepReviews, setKeepReviews) : null,
                            /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                className: "flex-1",
                                label: "Approvals",
                                value: !mayReview ? 'Not recorded: recording approvals needs the verdict permission on this program' : keepReviews ? `${n(plan.reviewsNew)} recorded${n(plan.reviewsExisting) ? ` (${n(plan.reviewsExisting)} already)` : ''}` : `${n(plan.reviewsNew)} left out`
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            adjusted ? kindTick('adjusted', 'adjusted dates', keepAdjusted, setKeepAdjusted) : null,
                            /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                className: "flex-1",
                                label: "Adjusted dates",
                                value: keepAdjusted ? `${n(plan.moves)} moved · ${n(plan.extensionsInstant) ? `${n(plan.extensionsInstant)} extended now (instant: ${plan.instantApprover}; late turns on time)` : `${n(plan.extensions)} proposed for a second approver (late would turn on time)`} · ${n(plan.rolls)} weekend/holiday rolls (the program's roll setting, not moved)` : `${adjusted} left as they are`
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                        label: "Questions",
                        value: `${n(plan.conflicts)} records not filed as stated · ${n(output.filedWithoutLink)} attested with no document · ${n(output.notFiledYet)} with no submission yet`
                    }),
                    eventCount || n(output.eventsUnknown) ? /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                        label: "Events",
                        value: `${eventCount ? eventGroups.map((g)=>`${n(g.count)} ${g.name}`).join(', ') : 'none'} dated from the records' due dates, logged on Accept${n(output.eventsExisting) ? ` (${n(output.eventsExisting)} already logged)` : ''}${n(output.eventsUnknown) ? ` · ${n(output.eventsUnknown)} records state no date an event can be read from` : ''}`
                    }) : null,
                    output.complete ? /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                        label: "Never filed",
                        value: `${n(output.unfiledPastDue)} past-due dates of these teams the evidence never mentions${output.asOf ? ` (evidence as of ${output.asOf})` : ''}`
                    }) : /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                        label: "Gaps",
                        value: "Partial: this evidence is not the client's whole record, so its gap batch proposes no waivers for dates it leaves out"
                    })
                ]
            }),
            shapes.length ? /*#__PURE__*/ _jsxs("details", {
                className: "border-t border-border px-4 py-2",
                children: [
                    /*#__PURE__*/ _jsx("summary", {
                        className: "cursor-pointer",
                        children: /*#__PURE__*/ _jsxs(SpecraText, {
                            size: "meta",
                            as: "span",
                            children: [
                                "Records not matched, by kind (",
                                n(output.unmatched),
                                "): add the deliverable, or explain or waive it"
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraDataTable, {
                        columns: [
                            {
                                id: 'rows',
                                header: 'Rows',
                                cell: (r)=>String(r.rows ?? 0)
                            },
                            {
                                id: 'reason',
                                header: 'Why',
                                cell: (r)=>REASONS[r.reason ?? ''] ?? r.reason
                            },
                            {
                                id: 'team',
                                header: 'Team',
                                cell: (r)=>r.team || '—'
                            },
                            {
                                id: 'title',
                                header: 'Deliverable',
                                cell: (r)=>r.title || '—'
                            },
                            {
                                id: 'detail',
                                header: 'Detail',
                                cell: (r)=>r.detail || ''
                            }
                        ],
                        rows: shapes,
                        getRowId: (r)=>r.id,
                        framed: false
                    })
                ]
            }) : null,
            eventCount ? /*#__PURE__*/ _jsxs("details", {
                className: "border-t border-border px-4 py-2",
                "data-testid": Testids.assistant.evidenceBatchEvents,
                children: [
                    /*#__PURE__*/ _jsx("summary", {
                        className: "cursor-pointer",
                        children: /*#__PURE__*/ _jsxs(SpecraText, {
                            size: "meta",
                            as: "span",
                            children: [
                                "Events the records date (",
                                eventCount,
                                "): each record's due date less its deliverable's offset, never its filing date"
                            ]
                        })
                    }),
                    eventGroups.map((g)=>/*#__PURE__*/ _jsxs("details", {
                            className: "py-1 pl-3",
                            children: [
                                /*#__PURE__*/ _jsx("summary", {
                                    className: "cursor-pointer",
                                    children: /*#__PURE__*/ _jsxs(SpecraText, {
                                        size: "meta",
                                        as: "span",
                                        children: [
                                            n(g.count),
                                            " ",
                                            g.name
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SpecraDataTable, {
                                    columns: [
                                        {
                                            id: 'on',
                                            header: 'Held on',
                                            cell: (r)=>r.on || '—'
                                        },
                                        {
                                            id: 'team',
                                            header: 'Team',
                                            cell: (r)=>r.team || '—'
                                        },
                                        {
                                            id: 'quote',
                                            header: 'Record',
                                            cell: (r)=>r.quote || (r.rows ?? []).join(', ')
                                        }
                                    ],
                                    rows: (g.events ?? []).map((e, i)=>({
                                            id: String(i),
                                            ...e
                                        })),
                                    getRowId: (r)=>r.id,
                                    framed: false
                                })
                            ]
                        }, g.name))
                ]
            }) : null,
            unfiled.length ? /*#__PURE__*/ _jsxs("details", {
                className: "border-t border-border px-4 py-2",
                children: [
                    /*#__PURE__*/ _jsx("summary", {
                        className: "cursor-pointer",
                        children: /*#__PURE__*/ _jsxs(SpecraText, {
                            size: "meta",
                            as: "span",
                            children: [
                                "Past due, never filed (",
                                n(output.unfiledPastDue),
                                ")"
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraDataTable, {
                        columns: [
                            {
                                id: 'due',
                                header: 'Due',
                                cell: (r)=>r.dueOn || '—'
                            },
                            {
                                id: 'team',
                                header: 'Team',
                                cell: (r)=>r.team || '—'
                            },
                            {
                                id: 'deliverable',
                                header: 'Deliverable',
                                cell: (r)=>r.deliverable || '—'
                            }
                        ],
                        rows: unfiled,
                        getRowId: (r)=>r.id,
                        framed: false
                    })
                ]
            }) : null,
            error ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                variant: "band",
                tone: "danger",
                "data-testid": Testids.assistant.evidenceBatchResult,
                children: error
            }) : null,
            output.runId && (status === 'pending' || status === 'working') ? /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-2 border-t border-border bg-muted/40 px-4 py-3",
                children: [
                    /*#__PURE__*/ _jsxs(SpecraButton, {
                        "data-testid": Testids.assistant.evidenceBatchAccept,
                        onClick: ()=>void decide('accepted'),
                        disabled: !decidable || !runChecked,
                        isLoading: status === 'working' || !runChecked,
                        iconLeft: /*#__PURE__*/ _jsx(Check, {
                            className: "h-4 w-4"
                        }),
                        children: [
                            "File ",
                            n(plan.submissionsNew),
                            " submissions",
                            eventCount ? ` · log ${eventCount} events` : ''
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        "data-testid": Testids.assistant.evidenceBatchReject,
                        onClick: ()=>void decide('rejected'),
                        disabled: !decidable || status === 'working',
                        children: "Don't"
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "micro",
                        tone: "muted",
                        className: "ml-auto",
                        children: decidable ? 'Nothing is filed until you choose.' : 'This earlier proposal has no saved review status. Ask for it again.'
                    })
                ]
            }) : null,
            !output.runId ? /*#__PURE__*/ _jsx("div", {
                className: "border-t border-border bg-muted/40 px-4 py-3",
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    size: "micro",
                    tone: "muted",
                    children: "No record can be filed yet; the questions above say why."
                })
            }) : null,
            accepted ? /*#__PURE__*/ _jsxs("div", {
                "data-testid": Testids.assistant.evidenceBatchResult,
                className: "flex items-start gap-2 border-t border-border bg-muted/40 px-4 py-3",
                children: [
                    /*#__PURE__*/ _jsx(Check, {
                        className: "mt-0.5 h-3.5 w-3.5 shrink-0 text-success",
                        "aria-hidden": true
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "micro",
                        className: "font-medium",
                        children: receipt || 'Filed.'
                    })
                ]
            }) : null,
            status === 'rejected' ? /*#__PURE__*/ _jsx("div", {
                className: "border-t border-border bg-muted/40 px-4 py-3",
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    size: "micro",
                    tone: "muted",
                    children: "Not filed — nothing changed."
                })
            }) : null,
            /*#__PURE__*/ _jsx(EvidenceGapsSection, {
                output: output,
                toolCallId: toolCallId,
                withReviews: withReviews
            })
        ]
    });
}
